// Таймер в кадре ролика: крупная дата внизу и полоска окна под ней.
//
// Чистая часть — текст, доли полоски, габариты плашки. Рисует их
// `exportImage.drawTimer`, а какие сутки в кадре — решает `videoTimeline`.
//
// См. docs/plans/2026-08-16-video-drift-timeline-design.md

import { formatDay, getLang } from '../i18n'
import { daysBetween } from '../models/driftDay'

/** Высота полоски окна, px (в координатах готового кадра). */
export const TIMER_BAR_H = 6
/** Поля плашки и высота строки даты. */
export const TIMER_PAD = 12
export const TIMER_LINE_H = 30
/** Отступ плашки от нижнего края. */
export const TIMER_BOTTOM = 18

/** Шаг поля течений, при котором час в кадре что-то значит. */
export const SIX_HOUR_STEP = 6
/** Срезов в сутках у шестичасового поля: 00/06/12/18 UTC. */
const SLICES_PER_DAY = 4

/** Что таймер показывает в этом кадре. */
export interface TimerFrame {
  /** Дата кадра, YYYY-MM-DD. */
  date: string
  /** Час среза поля течений (0/6/12/18) или null — поле суточное, часа нет. */
  hour?: number | null
  /** Края записанного окна. */
  from: string
  to: string
  /** Сегодня — по нему на полоске стоит метка «дальше прогноз». */
  today: string
}

/** Час среза для доли суток. `null` — поле суточное, часу взяться неоткуда.
 *
 *  Округление ВНИЗ: срез назван тем состоянием, которое уже наступило. Вверх он
 *  обещал бы то, чего в кадре ещё нет.
 *
 *  Доля 1 остаётся в СВОИХ сутках (18:00, не 00:00 следующих): сутки кадра
 *  выбраны раньше и по той же доле (`framePlan`), и пятый срез разошёлся бы с
 *  датой рядом в той же подписи. */
export function sliceHour(frac: number, stepH: number): number | null {
  if (stepH !== SIX_HOUR_STEP) return null
  const f = Math.min(1, Math.max(0, frac))
  const slice = Math.min(SLICES_PER_DAY - 1, Math.floor(f * SLICES_PER_DAY))
  return slice * SIX_HOUR_STEP
}

/** Подпись даты кадра — и часа среза, если поле шестичасовое.
 *
 *  Тем же `formatDay`, что и весь кабинет: у ru и en разный порядок частей, и
 *  собранная руками строка была бы вторым, неявным каталогом.
 *
 *  **Час появился не для красоты.** До 16.08.2026 подпись была датой всегда, и
 *  честно: поле течений лежало одно, суточное среднее, — «00:00» повторялось бы
 *  весь ролик, притворяясь точностью. С `currents_6h` усик считается по четырём
 *  состояниям на сутки, и час называет тот срез, который реально лежит в базе.
 *  На суточном фолбэке (`meta.currents_step_h = 24`) час не выводится вовсе.
 *
 *  Часы 24-часовые в обоих языках: срезы так и названы у самого продукта
 *  (00/06/12/18 UTC), и «6:00 AM» не совпало бы ни с ними, ни с логами
 *  загрузки, по которым сверяют кадр. */
export function timerLabel(frame: TimerFrame): string {
  const day = formatDay(getLang(), frame.date)
  if (frame.hour == null) return day
  const [y, m, d] = frame.date.split('-').map(Number)
  const clock = new Intl.DateTimeFormat(getLang(), {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d, frame.hour)))
  return `${day}, ${clock} UTC`
}

/** Доли полоски: где бегунок и где сегодня. `today: null` — сегодня вне окна. */
export function timerBarMarks(frame: TimerFrame): { head: number; today: number | null } {
  const span = daysBetween(frame.from, frame.to)
  if (span <= 0) return { head: 0, today: 0 }
  const share = (date: string) => daysBetween(frame.from, date) / span
  const head = Math.min(1, Math.max(0, share(frame.date)))
  const todayShare = share(frame.today)
  // Метку за краем окна НЕ прижимаем к его концу: прижатая, она утверждала бы,
  // что факт кончается ровно здесь, — а он кончился раньше или ещё не кончился.
  return { head, today: todayShare < 0 || todayShare > 1 ? null : todayShare }
}

export interface TimerBox {
  x: number
  y: number
  width: number
  height: number
}

/** Где стоит плашка таймера: внизу по центру кадра.
 *
 *  Ширину задаёт измеренный текст, но кадр она не перерастает — на узком кадре
 *  плашка иначе вылезала бы за оба края и подпись обрезалась бы с обеих сторон. */
export function timerLayout(
  frameWidth: number,
  frameHeight: number,
  textWidth: number,
): TimerBox {
  const width = Math.min(frameWidth, Math.ceil(textWidth) + TIMER_PAD * 4)
  const height = TIMER_LINE_H + TIMER_BAR_H + TIMER_PAD * 2
  return {
    x: Math.max(0, Math.round((frameWidth - width) / 2)),
    y: Math.round(frameHeight - height - TIMER_BOTTOM),
    width,
    height,
  }
}
